/**
 * Inspection Types Tab - New Implementation
 *
 * Manages inspection types and their form fields with drag-and-drop.
 * Simplified without language dictionary - uses direct field values.
 */
import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
	Box,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Typography,
	Divider,
	Paper,
	IconButton,
	Chip,
	Button,
	Menu,
	MenuItem,
	FormControlLabel,
	Switch,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
} from "@mui/material";
import {
	Add as AddIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
	DragIndicator as DragIcon,
	MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import {
	selectInspection,
	updateFormField,
	addFormField,
	reorderFormField,
	addInspectionType,
	createInspectionType,
	updateInspection,
	removeFormField,
} from "../redux/slice";
import FormFieldEditDialog from "../components/inspectionTypes/formFieldEditDialog.jsx";
import AddInspectionDialog from "../components/inspectionTypes/addInspectionDialog.jsx";
import CreateInspectionDialog from "../components/inspectionTypes/createInspectionDialog.jsx";

// Blank form field templates
const BLANK_FORM_FIELDS = [
	{ id: "checkbox", name: "Checkbox", type: "bool" },
	{ id: "dropdown", name: "Select (dropdown)", type: "select" },
	{ id: "text", name: "Text field", type: "text" },
	{ id: "photo", name: "Camera / Photos", type: "photo" },
];

// Premade field templates
const PREMADE_FIELDS = [
	{ id: "odometer", name: "Odometer", fieldType: "int", hint: "Value reported on the odometer" },
	{ id: "engine-hours", name: "Engine Hours", fieldType: "int", hint: "Engine hours as reported on dash" },
	{ id: "fuel-level", name: "Fuel Level", fieldType: "int", hint: "Estimated fuel in tank" },
	{ id: "hazmat", name: "Hazmat", fieldType: "bool", hint: "Do you have hazardous material on board" },
	{ id: "route-number", name: "Route Number", fieldType: "int", hint: "Route number" },
];

const InspectionTypesTab = forwardRef((props, ref) => {
	const dispatch = useDispatch();
	const inspectionListRef = useRef(null);
	const { inspectionDetails } = useSelector(
		(state) => state.inspectionBuilderRedux.config
	);
	// Alias for compatibility with existing component code
	const inspectionTypes = inspectionDetails;
	const { selectedInspectionId } = useSelector(
		(state) => state.inspectionBuilderRedux.ui
	);
	// Check if config has been loaded (originalConfig is set after loadConfig completes)
	const { loading, originalConfig } = useSelector(
		(state) => state.inspectionBuilderRedux
	);
	const configLoaded = originalConfig !== null;

	// Dialog states
	const [addMenuAnchor, setAddMenuAnchor] = useState(null);
	const [addFromLibraryDialogOpen, setAddFromLibraryDialogOpen] = useState(false);
	const [createNewDialogOpen, setCreateNewDialogOpen] = useState(false);

	// Edit dialog state
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editingField, setEditingField] = useState(null);
	const [editingFieldIndex, setEditingFieldIndex] = useState(null);

	// Delete dialog state
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deletingField, setDeletingField] = useState(null);

	// Auto-scroll to selected inspection
	const prevSelectedInspectionId = useRef(selectedInspectionId);
	const hasScrolledOnMount = useRef(false);

	useEffect(() => {
		const shouldScroll =
			selectedInspectionId &&
			(selectedInspectionId !== prevSelectedInspectionId.current ||
				!hasScrolledOnMount.current);

		if (shouldScroll) {
			hasScrolledOnMount.current = true;
			const timer = setTimeout(() => {
				if (!inspectionListRef.current) return;

				const listItems =
					inspectionListRef.current.querySelectorAll("[data-inspection-id]");
				const targetItem = Array.from(listItems).find(
					(item) => item.getAttribute("data-inspection-id") === selectedInspectionId
				);

				if (targetItem) {
					const scrollContainer = inspectionListRef.current.parentElement;
					if (!scrollContainer) return;

					const containerRect = scrollContainer.getBoundingClientRect();
					const itemRect = targetItem.getBoundingClientRect();
					const scrollTop = scrollContainer.scrollTop;
					const itemOffset = itemRect.top - containerRect.top + scrollTop;
					const centerOffset = (containerRect.height - itemRect.height) / 2;

					scrollContainer.scrollTo({
						top: itemOffset - centerOffset,
						behavior: "smooth",
					});

					const button = targetItem.querySelector(".MuiListItemButton-root");
					if (button) {
						button.style.transition = "background-color 0.3s ease";
						button.style.backgroundColor = "rgba(25, 118, 210, 0.2)";
						setTimeout(() => {
							button.style.backgroundColor = "";
						}, 1500);
					}
				}
			}, 200);

			return () => clearTimeout(timer);
		}

		prevSelectedInspectionId.current = selectedInspectionId;
	}, [selectedInspectionId]);

	// Expose scroll method to parent
	useImperativeHandle(ref, () => ({
		scrollToInspection: (inspectionId) => {
			// Select the inspection first
			dispatch(selectInspection(inspectionId));

			if (!inspectionListRef.current) return;

			const listItems =
				inspectionListRef.current.querySelectorAll("[data-inspection-id]");
			const targetItem = Array.from(listItems).find(
				(item) => item.getAttribute("data-inspection-id") === inspectionId
			);

			if (targetItem) {
				const scrollContainer = inspectionListRef.current.parentElement;
				const containerRect = scrollContainer.getBoundingClientRect();
				const itemRect = targetItem.getBoundingClientRect();
				const scrollTop = scrollContainer.scrollTop;
				const itemOffset = itemRect.top - containerRect.top + scrollTop;
				const centerOffset = (containerRect.height - itemRect.height) / 2;

				scrollContainer.scrollTo({
					top: itemOffset - centerOffset,
					behavior: "smooth",
				});

				const button = targetItem.querySelector(".MuiListItemButton-root");
				if (button) {
					button.style.transition = "background-color 0.3s ease";
					button.style.backgroundColor = "rgba(25, 118, 210, 0.2)";
					setTimeout(() => {
						button.style.backgroundColor = "";
					}, 1500);
				}
			}
		},
	}));

	// Drag and drop handler
	const handleDragEnd = (result) => {
		const { source, destination, draggableId } = result;

		if (!destination) return;
		if (
			source.droppableId === destination.droppableId &&
			source.index === destination.index
		)
			return;

		// Handle drag from premade fields to form fields
		if (
			source.droppableId === "premade-fields" &&
			destination.droppableId === "form-fields"
		) {
			const premadeField = PREMADE_FIELDS.find((f) => f.id === draggableId);
			if (!premadeField) return;

			const newField = {
				fieldId: uuidv4(),
				fieldName: premadeField.name,
				fieldType: premadeField.fieldType,
				hint: premadeField.hint,
				required: false,
			};

			dispatch(
				addFormField({
					inspectionId: selectedInspectionId,
					field: newField,
					index: destination.index,
				})
			);
		}

		// Handle drag from blank fields to form fields
		if (
			source.droppableId === "blank-fields" &&
			destination.droppableId === "form-fields"
		) {
			const fieldId = draggableId.replace("blank-", "");
			const blankField = BLANK_FORM_FIELDS.find((f) => f.id === fieldId);
			if (!blankField) return;

			const newField = {
				fieldId: uuidv4(),
				fieldName: blankField.name,
				fieldType: blankField.type,
				hint: "",
				required: false,
			};

			// Initialize options array for dropdown fields
			if (blankField.type === "select") {
				newField.options = ["Change me", ""];
			}

			dispatch(
				addFormField({
					inspectionId: selectedInspectionId,
					field: newField,
					index: destination.index,
				})
			);
		}

		// Handle reordering within form fields
		if (
			source.droppableId === "form-fields" &&
			destination.droppableId === "form-fields"
		) {
			dispatch(
				reorderFormField({
					inspectionId: selectedInspectionId,
					fromIndex: source.index,
					toIndex: destination.index,
				})
			);
		}
	};

	// Auto-select first inspection if none selected
	useEffect(() => {
		if (!selectedInspectionId && inspectionTypes && inspectionTypes.length > 0) {
			const firstId = inspectionTypes[0].typeId || inspectionTypes[0].detailType;
			dispatch(selectInspection(firstId));
		}
	}, [inspectionTypes, selectedInspectionId, dispatch]);

	const selectedInspection = inspectionTypes?.find(
		(inspection) =>
			(inspection.typeId || inspection.detailType) === selectedInspectionId
	);

	// Get inspection name - DIRECT field access
	const getInspectionName = (inspection) => {
		return inspection.detailType || `Inspection ${inspection.typeId}`;
	};

	// Get field type display name
	const getFieldTypeDisplay = (field) => {
		const fieldType = field.fieldType;
		const typeMap = {
			int: "Number",
			float: "Number (decimal)",
			text: "Text",
			select: "Dropdown",
			bool: "Checkbox",
			photo: "Photo",
		};
		return typeMap[fieldType] || fieldType;
	};

	// Edit field handler
	const handleEditField = (field, index) => {
		setEditingField(field);
		setEditingFieldIndex(index);
		setEditDialogOpen(true);
	};

	// Delete field handler
	const handleDeleteField = (field) => {
		setDeletingField(field);
		setDeleteDialogOpen(true);
	};

	// Confirm delete handler
	const handleConfirmDelete = () => {
		if (selectedInspection && deletingField) {
			dispatch(
				removeFormField({
					inspectionId: selectedInspectionId,
					fieldId: deletingField.fieldId,
				})
			);
		}
		setDeleteDialogOpen(false);
		setDeletingField(null);
	};

	// Save edited field
	const handleSaveField = (updatedField) => {
		if (selectedInspection && editingField) {
			const updates = {
				fieldName: updatedField.fieldName,
				fieldType: updatedField.fieldType,
				hint: updatedField.hint,
				required: updatedField.required,
			};
			// Include options for dropdown fields
			if (updatedField.options) {
				updates.options = updatedField.options;
			}
			dispatch(
				updateFormField({
					inspectionId: selectedInspectionId,
					fieldId: editingField.fieldId,
					updates,
				})
			);
		}

		setEditDialogOpen(false);
		setEditingField(null);
		setEditingFieldIndex(null);
	};

	// Cancel editing
	const handleCancelEdit = () => {
		setEditDialogOpen(false);
		setEditingField(null);
		setEditingFieldIndex(null);
	};

	// Handle add inspection menu
	const handleAddMenuClick = (event) => {
		setAddMenuAnchor(event.currentTarget);
	};

	const handleAddMenuClose = () => {
		setAddMenuAnchor(null);
	};

	const handleCreateNew = () => {
		handleAddMenuClose();
		setCreateNewDialogOpen(true);
	};

	const handleAddFromLibrary = () => {
		handleAddMenuClose();
		setAddFromLibraryDialogOpen(true);
	};

	// Handle adding inspection from library
	const handleAddInspection = (inspectionTemplate) => {
		dispatch(addInspectionType({ inspection: inspectionTemplate }));
	};

	// Handle creating new inspection
	const handleCreateInspection = (formData) => {
		dispatch(createInspectionType(formData));
	};

	// Get existing inspection names for validation
	const existingInspectionNames = inspectionTypes?.map((inspection) =>
		getInspectionName(inspection)
	) || [];

	// Handle toggling inspection properties
	const handleToggleInspectionProperty = (property) => (event) => {
		if (!selectedInspection) return;

		const inspectionIndex = inspectionTypes.findIndex(
			(insp) =>
				(insp.typeId || insp.detailType) ===
				(selectedInspection.typeId || selectedInspection.detailType)
		);

		if (inspectionIndex !== -1) {
			const updatedInspection = {
				...selectedInspection,
				[property]: event.target.checked,
			};

			dispatch(
				updateInspection({
					inspectionIndex,
					inspection: updatedInspection,
				})
			);
		}
	};

	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Box
				className="inspection-types-tab"
				sx={{ display: "flex", gap: 3, height: "600px" }}
			>
				{/* Left Column - Inspection Types List */}
				<Box
					sx={{
						width: 280,
						borderRight: 1,
						borderColor: "divider",
						display: "flex",
						flexDirection: "column",
						overflowY: "auto",
					}}
				>
					<Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography variant="h6">Inspections</Typography>
							<IconButton
								size="small"
								color="primary"
								onClick={handleAddMenuClick}
								aria-label="Add inspection"
							>
								<AddIcon />
							</IconButton>
						</Box>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Select an inspection to edit form fields
						</Typography>
					</Box>
					<Divider />

					<List sx={{ pt: 0 }} ref={inspectionListRef}>
						{inspectionTypes?.map((inspection) => {
							const id = inspection.typeId || inspection.detailType;
							return (
								<ListItem key={id} disablePadding data-inspection-id={id}>
									<ListItemButton
										selected={selectedInspectionId === id}
										onClick={() => dispatch(selectInspection(id))}
									>
										<ListItemText
											primary={
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
													<Box
														component="span"
														sx={{
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
														title={getInspectionName(inspection)}
													>
														{getInspectionName(inspection)}
													</Box>
													{inspection.legallyMandated && (
														<Box
															component="span"
															sx={{
																width: 6,
																height: 6,
																borderRadius: "50%",
																bgcolor: "error.main",
																flexShrink: 0,
															}}
														/>
													)}
												</Box>
											}
										/>
									</ListItemButton>
								</ListItem>
							);
						})}
					</List>

					{(!inspectionTypes || inspectionTypes.length === 0) && (
						<Box sx={{ p: 2, textAlign: "center" }}>
							{loading || !configLoaded ? (
								<CircularProgress size={24} />
							) : (
								<Typography variant="body2" color="text.secondary">
									No inspection types found
								</Typography>
							)}
						</Box>
					)}
				</Box>

				{/* Center Column - Form Fields */}
				<Box
					sx={{ flex: 1, display: "flex", flexDirection: "column", overflowY: "auto" }}
				>
					{selectedInspection ? (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
							<Box
								sx={{
									position: "sticky",
									top: 0,
									bgcolor: "background.paper",
									zIndex: 1,
									pb: 1,
								}}
							>
								<Typography
									variant="h6"
									sx={{
										mb: 0.5,
										wordBreak: "break-word",
										overflowWrap: "break-word"
									}}
									title={getInspectionName(selectedInspection)}
								>
									{getInspectionName(selectedInspection)}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{selectedInspection.formData?.length || 0} form fields
								</Typography>
							</Box>

							<Droppable droppableId="form-fields">
								{(provided, snapshot) => (
									<Box
										ref={provided.innerRef}
										{...provided.droppableProps}
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: 1,
											minHeight: 200,
											bgcolor: snapshot.isDraggingOver
												? "action.hover"
												: "transparent",
											transition: "background-color 0.2s ease",
											...((!selectedInspection.formData || selectedInspection.formData.length === 0) && {
												border: 2,
												borderStyle: "dashed",
												borderColor: snapshot.isDraggingOver ? "primary.main" : "divider",
												borderRadius: 1,
												alignItems: "center",
												justifyContent: "center",
											}),
										}}
									>
										{selectedInspection.formData && selectedInspection.formData.length > 0 ? (
											selectedInspection.formData.map((field, index) => {
												// Generate a unique key - always include index to guarantee uniqueness
												// even if fieldId is empty string or duplicate
												const uniqueKey = field.fieldId && field.fieldId.length > 0
													? `${field.fieldId}-${index}`
													: `field-idx-${index}`;
												return (
												<Draggable
													key={uniqueKey}
													draggableId={uniqueKey}
													index={index}
												>
													{(provided, snapshot) => (
														<Paper
															ref={provided.innerRef}
															{...provided.draggableProps}
															sx={{
																p: 2,
																border: 1,
																borderColor: snapshot.isDragging
																	? "primary.main"
																	: "divider",
																bgcolor: snapshot.isDragging
																	? "action.selected"
																	: "background.paper",
																"&:hover": {
																	borderColor: "primary.main",
																	bgcolor: "action.hover",
																},
																transition:
																	"border-color 0.2s ease, background-color 0.2s ease",
															}}
														>
															<Box sx={{ display: "flex", gap: 2 }}>
																{/* Drag handle */}
																<Box
																	{...provided.dragHandleProps}
																	sx={{
																		display: "flex",
																		flexDirection: "column",
																		gap: "2px",
																		alignItems: "center",
																		justifyContent: "center",
																		color: "text.disabled",
																		cursor: "grab",
																		"&:active": { cursor: "grabbing" },
																	}}
																>
																	<Box
																		sx={{
																			width: "4px",
																			height: "4px",
																			borderRadius: "50%",
																			bgcolor: "currentColor",
																		}}
																	/>
																	<Box
																		sx={{
																			width: "4px",
																			height: "4px",
																			borderRadius: "50%",
																			bgcolor: "currentColor",
																		}}
																	/>
																	<Box
																		sx={{
																			width: "4px",
																			height: "4px",
																			borderRadius: "50%",
																			bgcolor: "currentColor",
																		}}
																	/>
																	<Box
																		sx={{
																			width: "4px",
																			height: "4px",
																			borderRadius: "50%",
																			bgcolor: "currentColor",
																		}}
																	/>
																</Box>
																{/* Field content */}
																<Box sx={{ flex: 1 }}>
																	<Box
																		sx={{
																			display: "flex",
																			justifyContent: "space-between",
																			alignItems: "flex-start",
																			mb: 0.5,
																		}}
																	>
																		<Typography variant="body1" sx={{ fontWeight: 500 }}>
																			{field.fieldName || field.hint || "Unnamed Field"}
																		</Typography>
																		{field.required && (
																			<Chip
																				label="Required"
																				size="small"
																				color="primary"
																				sx={{ ml: 1 }}
																			/>
																		)}
																	</Box>

																	{field.hint && (
																		<Typography
																			variant="body2"
																			color="text.secondary"
																			sx={{ mb: 1 }}
																		>
																			{field.hint}
																		</Typography>
																	)}

																	<Box
																		sx={{ display: "flex", gap: 1, alignItems: "center" }}
																	>
																		<Chip
																			label={getFieldTypeDisplay(field)}
																			size="small"
																			variant="outlined"
																		/>
																	</Box>
																</Box>

																{/* Actions */}
																<Box sx={{ display: "flex", gap: 0.5 }}>
																	<IconButton
																		size="small"
																		aria-label="Edit field"
																		onClick={() => handleEditField(field, index)}
																		sx={{
																			width: 32,
																			height: 32,
																			borderRadius: "50%",
																		}}
																	>
																		<EditIcon fontSize="small" />
																	</IconButton>
																	<IconButton
																		size="small"
																		aria-label="Delete field"
																		onClick={() => handleDeleteField(field)}
																		sx={{
																			width: 32,
																			height: 32,
																			borderRadius: "50%",
																		}}
																	>
																		<DeleteIcon fontSize="small" />
																	</IconButton>
																</Box>
															</Box>
														</Paper>
													)}
												</Draggable>
											);
											})
										) : (
											<Typography variant="body2" color="text.secondary">
												No form fields yet. Drag from Form Library to add fields.
											</Typography>
										)}
										{provided.placeholder}
									</Box>
								)}
							</Droppable>

							{/* Inspection Properties Section */}
							<Divider sx={{ my: 2 }} />
							<Paper elevation={1} sx={{ p: 2 }}>
								<Typography variant="h6" sx={{ mb: 2 }}>
									Inspection Properties
								</Typography>
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
									<FormControlLabel
										control={
											<Switch
												checked={selectedInspection.legallyMandated || false}
												onChange={handleToggleInspectionProperty("legallyMandated")}
												size="small"
											/>
										}
										label={
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													Legally Mandated
												</Typography>
												<Typography variant="caption" color="text.secondary">
													DOT/regulatory compliance required
												</Typography>
											</Box>
										}
									/>
									<FormControlLabel
										control={
											<Switch
												checked={selectedInspection.enforceOrder || false}
												onChange={handleToggleInspectionProperty("enforceOrder")}
												size="small"
											/>
										}
										label={
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													Enforce Order
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Fields must be completed in sequence
												</Typography>
											</Box>
										}
									/>
									<FormControlLabel
										control={
											<Switch
												checked={selectedInspection.prepopulateDefects || false}
												onChange={handleToggleInspectionProperty("prepopulateDefects")}
												size="small"
											/>
										}
										label={
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													Prepopulate Defects
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Auto-populate from previous inspection
												</Typography>
											</Box>
										}
									/>
									<FormControlLabel
										control={
											<Switch
												checked={selectedInspection.zoneless || false}
												onChange={handleToggleInspectionProperty("zoneless")}
												size="small"
											/>
										}
										label={
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													Zoneless
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Not tied to specific zones
												</Typography>
											</Box>
										}
									/>
									<FormControlLabel
										control={
											<Switch
												checked={selectedInspection.associateDriver || false}
												onChange={handleToggleInspectionProperty("associateDriver")}
												size="small"
											/>
										}
										label={
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													Associate Driver
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Link to driver performing inspection
												</Typography>
											</Box>
										}
									/>
								</Box>
							</Paper>
						</Box>
					) : (
						<Box
							sx={{
								flex: 1,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							{loading || !configLoaded ? (
								<CircularProgress />
							) : (
								<Typography variant="h6" color="text.secondary">
									{!inspectionTypes || inspectionTypes.length === 0
										? "No inspection types available"
										: "Select an inspection type to view form fields"}
								</Typography>
							)}
						</Box>
					)}
				</Box>

				{/* Right Column - Form Library */}
				<Box
					sx={{
						width: 300,
						borderLeft: 1,
						borderColor: "divider",
						display: "flex",
						flexDirection: "column",
						overflowY: "auto",
					}}
				>
					<Box sx={{ p: 2, pb: 1 }}>
						<Typography variant="h6" sx={{ mb: 1 }}>
							Form library
						</Typography>
					</Box>
					<Divider />

					{/* Blank Form Fields Section */}
					<Box sx={{ p: 2 }}>
						<Droppable droppableId="blank-fields" isDropDisabled={true}>
							{(provided) => (
								<Box
									ref={provided.innerRef}
									{...provided.droppableProps}
									sx={{ display: "flex", flexDirection: "column", gap: 1 }}
								>
									{BLANK_FORM_FIELDS.map((field, index) => (
										<Draggable
											key={field.id}
											draggableId={`blank-${field.id}`}
											index={index}
										>
											{(provided, snapshot) => (
												<Paper
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													sx={{
														p: 1.5,
														cursor: snapshot.isDragging ? "grabbing" : "grab",
														border: 1,
														borderColor: snapshot.isDragging
															? "primary.main"
															: "divider",
														bgcolor: snapshot.isDragging ? "primary.light" : "grey.100",
														opacity: snapshot.isDragging ? 0.8 : 1,
														"&:hover": {
															bgcolor: "grey.200",
															borderColor: "primary.main",
														},
														transition:
															"border-color 0.2s ease, background-color 0.2s ease",
													}}
												>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<DragIcon fontSize="small" sx={{ color: "text.disabled" }} />
														<Typography variant="body2">{field.name}</Typography>
													</Box>
												</Paper>
											)}
										</Draggable>
									))}
									{provided.placeholder}
								</Box>
							)}
						</Droppable>
					</Box>
					<Divider />

					{/* Premade Fields Section */}
					<Box sx={{ p: 2, flex: 1, overflow: "auto" }}>
						<Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
							Premade fields
						</Typography>

						<Droppable droppableId="premade-fields" isDropDisabled={true}>
							{(provided) => (
								<Box
									ref={provided.innerRef}
									{...provided.droppableProps}
									sx={{ display: "flex", flexDirection: "column", gap: 1 }}
								>
									{PREMADE_FIELDS.map((field, index) => (
										<Draggable key={field.id} draggableId={field.id} index={index}>
											{(provided, snapshot) => (
												<Paper
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													sx={{
														p: 1.5,
														cursor: snapshot.isDragging ? "grabbing" : "grab",
														border: 1,
														borderColor: snapshot.isDragging
															? "primary.main"
															: "divider",
														bgcolor: snapshot.isDragging ? "primary.light" : "grey.50",
														opacity: snapshot.isDragging ? 0.8 : 1,
														"&:hover": {
															bgcolor: "grey.100",
															borderColor: "primary.main",
														},
														transition:
															"border-color 0.2s ease, background-color 0.2s ease",
													}}
												>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<DragIcon fontSize="small" sx={{ color: "text.disabled" }} />
														<Box sx={{ flex: 1 }}>
															<Typography variant="body2" sx={{ fontWeight: 500 }}>
																{field.name}
															</Typography>
															<Typography variant="caption" color="text.secondary">
																{field.fieldType}
															</Typography>
														</Box>
														<IconButton size="small" sx={{ color: "text.secondary" }}>
															<MoreVertIcon fontSize="small" />
														</IconButton>
													</Box>
												</Paper>
											)}
										</Draggable>
									))}
									{provided.placeholder}
								</Box>
							)}
						</Droppable>
					</Box>
				</Box>

				{/* Edit Field Dialog */}
				<FormFieldEditDialog
					open={editDialogOpen}
					field={editingField}
					onSave={handleSaveField}
					onCancel={handleCancelEdit}
				/>

				{/* Delete Confirmation Dialog */}
				<Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
					<DialogTitle>Delete Form Field?</DialogTitle>
					<DialogContent>
						<Typography>
							Are you sure you want to remove this field from the inspection? This action
							cannot be undone.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
						<Button onClick={handleConfirmDelete} color="error" variant="contained">
							Delete
						</Button>
					</DialogActions>
				</Dialog>

				{/* Add Inspection Menu */}
				<Menu
					anchorEl={addMenuAnchor}
					open={Boolean(addMenuAnchor)}
					onClose={handleAddMenuClose}
				>
					<MenuItem onClick={handleCreateNew}>Create New</MenuItem>
					<MenuItem onClick={handleAddFromLibrary}>Add from Library</MenuItem>
				</Menu>

				{/* Add from Library Dialog */}
				<AddInspectionDialog
					open={addFromLibraryDialogOpen}
					onClose={() => setAddFromLibraryDialogOpen(false)}
					onAdd={handleAddInspection}
				/>

				{/* Create New Dialog */}
				<CreateInspectionDialog
					open={createNewDialogOpen}
					onClose={() => setCreateNewDialogOpen(false)}
					onCreate={handleCreateInspection}
					existingNames={existingInspectionNames}
				/>
			</Box>
		</DragDropContext>
	);
});

InspectionTypesTab.displayName = "InspectionTypesTab";

export default InspectionTypesTab;
